<template>
  <form @submit.prevent="saveNewIssue">
    <div class="layout-issue">
      <HeaderBar
        v-if="!issueId"
        :modifiers="['transparent', 'more-height']"
      >
        {{ $t('issues.new_issue') }}
        <template #right>
          <router-link
            class="button"
            :to="defaultNavBackRoute"
            @click.passive="issueLocationStore.$reset()"
          >
            {{ $t('general.cancel') }}
          </router-link>
          <FormSubmitButton :isSaving="saving" />
        </template>
      </HeaderBar>
      <IssueNav
        v-if="issueId && initialIssue"
        :inspectionId="inspectionId"
        :issue="initialIssue"
        :navRouterPath="setNavRouterPath"
        :useRouterPath="setRouterPathStatus"
        @click.passive="issueLocationStore.$reset()"
      />
      <div
        v-if="!issueId"
        class="issue__section issue__section--lighter issue__section--bar"
      >
        <div class="issue__checkbox">
          <Checkbox
            id="new_issue"
            v-model="issueLocationStore.saveAndCreateNewIssue"
            :modifiers="['switch']"
          >
            <p class="typo-small-title">
              {{ $t('issues.new_issue_after_saving') }}
            </p>
          </Checkbox>
        </div>
      </div>

      <div class="issue">
        <div class="issue-details scroll-container">
          <template v-if="!loading">
            <div class="issue__title typo-title">
              <IssueEditField
                ref="textTitle"
                :canEdit="canEdit"
                :error="hasError('title')"
                field="title"
                :issue="issue"
                :showLabel="false"
                @input="validateInput('title')"
                @update="loadIssue"
              />
            </div>

            <div class="issue__location">
              <router-link
                :class="{
                  'issue__thumbnail': true,
                  'disabled': disableLocation
                }"
                :to="locationRouteName"
              >
                <MapPreview
                  v-if="!loadMap || !issue?.location?.location"
                  :emptyText="emptyText"
                />
                <div v-else class="layout-regions__map-wrapper">
                  <div class="layout-regions__map layout-regions__map--center">
                    <LeafletMap
                      ref="map"
                      :diagram="currentOperation?.diagram_identifier || issue?.location?.diagram_identifier"
                      :dragging="false"
                      :zooming="false"
                      @init="onMapInit(issue?.location?.location)"
                    />
                    <div class="layout-regions__overlay" />
                  </div>
                </div>
              </router-link>

              <div class="issue__content">
                <p :class="{ error: hasError('location') }">
                  <span v-if="!issue?.direct_region?.name && !issue.location.location">
                    {{ $t('issues.set_issue_location') }}
                  </span>
                  <span v-else>{{ $t('issues.issue_location') }}</span>
                </p>
                <div
                  :class="{
                    'issue__input issue__input--placeholder typo-ignore': true,
                    'issue__input--margin-top': issue?.direct_region?.name || issue.location.location,
                  }"
                >
                  <p
                    v-if="!issue?.direct_region?.name && !issue.location.location"
                    class="issue__input--margin-top"
                  >
                    {{ $t('issues.no_issue_location') }}
                  </p>
                  <p v-else>
                    {{ issue?.direct_region?.name }}
                  </p>
                </div>
                <router-link
                  :class="{
                    'button button--small button--full-width-mobile typo-button issue__input--margin-top': true,
                    'button--disabled': disableLocation
                  }"
                  :to="locationRouteName"
                >
                  {{ issue?.direct_region?.name || issue.location.location ? $t('issues.change_location') : $t('issues.set_location') }}
                </router-link>
              </div>
            </div>

            <div class="issue__body">
              <div class="issue__section typo-small-title">
                <IssueEditField
                  ref="textLocationDescription"
                  :canEdit="canEdit"
                  field="location_description"
                  :issue="issue"
                  :placeholder="$t('issues.unspecified')"
                  @update="loadIssue"
                />
              </div>
              <div
                :class="{
                  'issue__section issue__section-image issue__section--light issue__section--horizontal-fade': true,
                  'issue__image--error': !imageValid,
                }"
              >
                <div class="issue-field">
                  <div class="issue-field__wrapper">
                    <FileUploader
                      v-if="canEdit"
                      :acceptedFileTypes="acceptedFileTypes"
                      fileType="image"
                      :label="$t('issues.add_image')"
                      :modifiers="['always-show']"
                      @update-blob="saveBlob($event, 'image')"
                      @update-error="wrongFileType"
                      @update-reader="updateFileList($event, 'image')"
                    />
                    <div class="issue-field__header">
                      <div class="issue-field__title typo-small-title">
                        {{
                          pluralize(
                            issue.issue_images.length,
                            $t('issues.image'),
                            $t('issues.images')
                          )
                        }}
                      </div>
                    </div>
                    <div
                      :class="{
                        'issue-field__body': true,
                        'issue-field__body--padding-bottom': sortedImages?.length > 0
                      }"
                    >
                      <div
                        ref="imagesContainer"
                        class="issue__images"
                      />
                      <teleport to="body">
                        <ImageViewer
                          v-model:currentImage="viewingImage"
                          :currentOperation="currentOperation"
                          :images="sortedImages"
                          :issuePath="operationData.issueUrl"
                          :issue="issue"
                          @annotate-image="annotateImage"
                          @apply-damage-rating="applyDamageRating"
                          @clear-damage-rating="clearDamageRating"
                          @update-image="updateCurrentImage"
                        >
                          <p class="typo-ignore typo-title">
                            <strong>{{ issue.title }}</strong>
                          </p>
                          <p class="typo-ignore typo-tiny">
                            {{ $t('inspections.issue') }}: #{{ shortId }}
                          </p>
                        </ImageViewer>
                      </teleport>
                      <template
                        v-for="image in sortedImages"
                        :key="image.id"
                      >
                        <teleport
                          v-if="image.htmlElement"
                          :to="image.htmlElement"
                        >
                          <div
                            v-if="canEdit"
                            class="issue__image-menu"
                          >
                            <UseSvg
                              class="icon"
                              name="vertical_ellipsis"
                            />
                            <div
                              class="issue__image-menu-item"
                              @click="confirmDeleteDialog('image', image.id)"
                            >
                              <div class="issue__image-menu-item-content">
                                <UseSvg
                                  class="icon"
                                  name="trash_16"
                                />
                                {{ $t('issues.remove_image') }}
                              </div>
                            </div>
                          </div>

                          <div
                            v-if="image.ai_assessment"
                            class="issue__image-ai-assessment"
                          >
                            <AiIconSvg
                              class="issue__image-ai-assessment--icon"
                              @click="setAssessment(image)"
                            />
                          </div>
                        </teleport>
                      </template>
                      <p
                        v-if="issue.issue_images.length === 0"
                        class="typo-small-title"
                      >
                        {{ $t('issues.no_images') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <FormStandAloneErrorMessage
                v-if="!imageValid"
                :errorMessage="imageValidationMessage"
              />
              <div class="issue__section typo-small-title">
                <IssueEditField
                  ref="textDescription"
                  :canEdit="canEdit"
                  :error="hasError('description')"
                  field="description"
                  :issue="issue"
                  :placeholder="$t('issues.no_description')"
                  @input="validateInput('description')"
                  @update="loadIssue"
                />
                <IssueEditField
                  ref="textRecommendation"
                  :canEdit="canEdit"
                  :error="hasError('recommendation')"
                  field="recommendation"
                  :issue="issue"
                  :placeholder="$t('issues.no_recommendation')"
                  @input="validateInput('recommendation')"
                  @update="loadIssue"
                />
              </div>

              <div class="issue__section issue__section--light">
                <div class="issue__checkbox">
                  <Checkbox
                    id="requires_task"
                    v-model="issue.requires_task"
                    :disabled="!canResolve"
                    :modifiers="['switch']"
                    @update:model-value="update('requires_task', $event)"
                  >
                    <p class="typo-small-title">
                      {{ $t('issues.requires_tasks') }}
                    </p>
                  </Checkbox>
                </div>
              </div>

              <div
                v-if="issue.requires_task"
                class="issue__tasks issue__section issue__section--light"
              >
                <div class="issue__tasks-header">
                  <div class="issue-field">
                    <div class="issue-field__wrapper">
                      <div class="issue-field__header">
                        <div class="issue-field__title typo-small-title">
                          {{
                            pluralize(
                              issue.tasks.length,
                              $t('issues.task'),
                              $t('issues.tasks')
                            )
                          }}
                        </div>
                      </div>

                      <div
                        :class="{
                          'issue-field__body': true,
                          'issue-field__body--persist':
                            issue.tasks.length === 0,
                        }"
                      >
                        <button
                          v-show="canResolve"
                          :aria-label="$t('issues.add_task')"
                          class="issue__tasks-new"
                          type="button"
                          @click="createNewTask"
                        >
                          <UseSvg
                            class="icon icon--small"
                            name="plus_16"
                          />
                        </button>

                        <IssueTaskFormModal
                          v-if="newTask"
                          :errorSaving="errorSavingTask"
                          :initialTask="newTask"
                          :inspectionDate="initialIssue.inspection.date"
                          :issue="issue"
                          @cancel="newTask = null"
                          @submit="addNewTask"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="issue__tasks-content">
                  <IssueTask
                    v-for="task in issue.tasks.sort(byCreatedAt)"
                    :key="task.id"
                    :canEdit="!canResolve"
                    :errorSaving="errorSavingTask"
                    :issue="issue"
                    :open="toggleIssueModal"
                    :task="task"
                    useServiceToClose
                    @delete="confirmDeleteDialog('task', task.id)"
                    @mark-as="markTaskAs(task, $event)"
                    @update="updateTask(task, $event)"
                  />
                  <div
                    v-if="issue.tasks.length === 0"
                    class="
                      issue__tasks-content-empty issue__section--last
                      typo-small-text
                    "
                  >
                    {{ $t('empty_states.tasks_empty') }}
                  </div>
                </div>
                <div
                  v-if="issue.tasks.length !== 0"
                  class="issue__section issue__section--last"
                >
                  <div class="issue__checkbox--resolve">
                    <Checkbox
                      id="tasks_close_issue"
                      v-model="issue.tasks_close_issue"
                      :disabled="!canResolve"
                      @update:model-value="update('tasks_close_issue', $event)"
                    >
                      <div class="issue__checkbox--info">
                        <p class="typo-field-text">
                          {{ $t('tasks.completing_tasks') }}
                        </p>
                        <p class="typo-ignore typo-tiny">
                          {{ $t('tasks.marked_as_resolved') }}
                        </p>
                      </div>
                    </Checkbox>
                  </div>
                </div>
              </div>

              <!-- Document upload -->
              <div class="issue__section issue__section-document issue__section--light">
                <AttachmentUploader
                  :canEdit="canEdit"
                  v-model:documentList="issue.issue_documents"
                  @update-blob="saveBlob($event, 'attachment')"
                  @delete-file="removeAttachmentItem"
                />
              </div>
            </div>
          </template>
          <Loader v-if="loading" />
        </div>
        <div class="issue-ratings typo-small-title">
          <template v-if="!loading">
            <IssueRiskRating
              v-if="showRisk && issueId"
              :issue="issue"
            />
            <!-- Status -->
            <div
              v-if="issueId"
              class="issue-inline-field typo-small-title"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.status') }}
              </div>
              <div class="issue-inline-field__field">
                <Select
                  v-model="issue.status"
                  :disabled="!canResolve"
                  :items="statusOptions"
                  labelStyle="pill"
                  @update:model-value="update('status', $event)"
                />
              </div>
            </div>

            <!-- Type/Material -->
            <div
              :class="{
                'issue-inline-field typo-small-title': true,
                'issue-inline-field--error': hasError('element_material'),
              }"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.material') }}
              </div>
              <div class="issue-inline-field__field">
                <Select
                  :disabled="!canEdit"
                  :items="elementMaterialOptions"
                  :modelValue="issue.element_material"
                  :placeholder="$t('issues.unspecified')"
                  @update:model-value="updateElementMaterial"
                />
              </div>
            </div>

            <!-- Element Class -->
            <div
              :class="{
                'issue-inline-field typo-small-title': true,
                'issue-inline-field--error': hasError('element_class'),
              }"
            >
              <div class="issue-inline-field__title">
                {{ $t('issues.class') }}
              </div>
              <div class="issue-inline-field__field">
                <DescriptionSelect
                  v-if="elementClassOptions.length > 0"
                  v-model="issue.element_class"
                  :disabled="!canEdit"
                  :items="elementClassOptions"
                  :label="$t('issues.class')"
                  name="element_class"
                  :placeholder="$t('issues.unspecified')"
                  :removable="false"
                  selectClasses="issue-ratings__dropdown"
                  :simple="true"
                  @update:model-value="update('element_class', $event)"
                />
              </div>
            </div>

            <div class="issue-ratings__section">
              <div class="issue-inline-field issue-inline-field--heading">
                <p class="issue-inline-field__title">
                  {{ $t('issues.deterioration_mechanisms') }}
                </p>
                <div class="issue-inline-field__field">
                  <div
                    v-if="canEdit && Object.keys(deteriorationMechanisms).length > 0"
                    class="select select--input issue-ratings__dropdown"
                  >
                    <div class="dropdown">
                      <Popper
                        v-model:open="newRatingOpen"
                        placement="bottom-end"
                      >
                        <template #trigger>
                          <div class="dropdown__trigger">
                            <div class="select__trigger issue-ratings__drop-trigger">
                              <button
                                :aria-label="$t('issues.add_deterioration_mechanism_rating')"
                                class="select__label select__label--flex"
                                type="button"
                              >
                                <Plus16Svg />
                              </button>

                              <span class="select__arrow">
                                <UseSvg
                                  class="icon icon--small"
                                  name="arrow_down_16"
                                />
                              </span>
                            </div>
                          </div>
                        </template>

                        <div
                          v-if="!newRating"
                          class="menu menu--dropdown"
                        >
                          <ul class="menu__items">
                            <li
                              v-for="(detMech, name) in deteriorationMechanisms"
                              :key="name"
                              class="menu-item menu-item--dropdown"
                            >
                              <button
                                class="menu-item__link"
                                type="button"
                                @click="createNewRating(name, detMech)"
                              >
                                <span class="menu-item__label">{{
                                  $t(`deterioration_mechanisms.${name}.name`)
                                }}</span>
                              </button>
                            </li>
                          </ul>
                        </div>

                        <DescriptionSelectDropdown
                          v-if="newRating"
                          :addText="$t('issues.add_rating')"
                          iconName="rating_circle"
                          :items="ratingItems(newRating)"
                          :label="$t(`deterioration_mechanisms.${newRating.name}.name`)"
                          name="new_rating"
                          :ratingScale="true"
                          :removable="false"
                          @cancel="cancelNewRating"
                          @update:model-value="addRating"
                        />
                      </Popper>
                    </div>
                  </div>
                </div>
              </div>

              <div class="issue__ratings-container">
                <div
                  v-for="rating in issue.deterioration_mechanism_ratings"
                  :key="rating.id"
                  class="issue-inline-field"
                >
                  <div class="issue-inline-field__title">
                    <a
                      v-if="hasAcceptedImage(rating)"
                      class="issue-inline-field__calculated-icon"
                      @click.prevent="openDamageCalculation(acceptedImage.id)"
                    >
                      <WrenchIcon />
                    </a>
                    {{ $t(`deterioration_mechanisms.${rating.name}.name`) }}
                  </div>
                  <div class="issue-inline-field__field">
                    <DescriptionSelect
                      :disabled="!canEdit"
                      iconName="rating_circle"
                      :items="ratingItems(rating)"
                      :label="$t(`deterioration_mechanisms.${rating.name}.name`)"
                      :modelValue="rating.value"
                      :name="rating.name"
                      :ratingScale="true"
                      :selectIcon="true"
                      selectClasses="issue-ratings__dropdown"
                      @remove="removeRating(rating)"
                      @update:model-value="updateRating(rating, $event)"
                    />
                  </div>
                </div>
                <div
                  v-if="issue.deterioration_mechanism_ratings.length === 0"
                  class="issue-inline-field issue-inline-field--empty"
                >
                  <div class="issue-inline-field__icon">
                    <ExclamationTriangleSvg />
                  </div>
                  <div class="issue-inline-field__title">
                    {{ $t('issues.none_specified') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="issue-ratings__section">
              <div class="issue-inline-field issue-inline-field--heading">
                <p class="issue-inline-field__title">
                  {{ $t('issues.risk_factors') }}
                </p>
              </div>
              <div
                :class="{
                  'issue-inline-field typo-small-title': true,
                  'issue-inline-field--error': hasError(
                    'overall_condition_rating'
                  ),
                }"
              >
                <div class="issue-inline-field__title">
                  {{ $t('issues.overall_condition') }}
                </div>
                <div class="issue-inline-field__field">
                  <DescriptionSelect
                    :disabled="!canEdit"
                    iconName="rating_square"
                    :items="overallConditionOptions"
                    :label="$t('issues.overall_condition')"
                    :modelValue="issue.overall_condition_rating"
                    name="overall_condition_rating"
                    placeholder="-"
                    :ratingScale="true"
                    :removable="false"
                    :selectIcon="true"
                    selectClasses="issue-ratings__dropdown"
                    @update:model-value="update('overall_condition_rating', $event)"
                  />
                </div>
              </div>
              <div
                :class="{
                  'issue-inline-field typo-small-title': true,
                  'issue-inline-field--error': hasError('priority'),
                }"
              >
                <div class="issue-inline-field__title">
                  {{ $t('issues.repair_priority') }}
                </div>
                <div class="issue-inline-field__field">
                  <DescriptionSelect
                    :disabled="!canEdit"
                    iconName="rating_triangle"
                    :items="repairPriorityOptions"
                    :label="$t('issues.repair_priority')"
                    :modelValue="issue.priority"
                    name="priority"
                    placeholder="-"
                    :ratingScale="true"
                    :removable="false"
                    :selectIcon="true"
                    selectClasses="issue-ratings__dropdown"
                    @update:model-value="update('priority', $event)"
                  />
                </div>
              </div>
            </div>

            <div v-if="issueId && currentOperation?.policy?.sap_v1" class="issue-ratings__section">
              <div class="issue-inline-field issue-inline-field--heading">
                <p class="issue-inline-field__title">
                  {{ $t('regions.sap.sap_details') }}
                </p>
              </div>
              <div class="form__group-inner">
                <template v-if="issue?.kumba_sap_information?.data">
                  <FormGroup
                    v-for="[name, value] of cleanSapData(issue.kumba_sap_information.data, sapInformationList)"
                    :key="name"
                    :label="$t(`regions.sap.${name}`)"
                    :value="value?.toString() || ''"
                    :modifiers="['narrow']"
                    :isHidden="!value"
                  />
                </template>
                <template v-if="issue?.kumba_issue_notification?.data">
                  <template v-if="issue?.kumba_issue_notification?.data?.status?.toLowerCase() !== 'error'">
                    <FormGroup
                      v-for="[name, value] of cleanSapData(issue.kumba_issue_notification.data, sapNotificationList, false)"
                      :key="name"
                      :label="$t(`regions.sap.${name}`)"
                      :value="value?.toString() || ''"
                      :modifiers="['narrow']"
                      :isPending="!value"
                    />
                  </template>
                  <!-- If the status is an error only show the error -->
                  <FormGroup
                    v-else-if="issue?.kumba_issue_notification?.data?.status?.toLowerCase() !== 'success'"
                    key="status"
                    :label="$t(`regions.sap.status`)"
                    :value="issue?.kumba_issue_notification?.data?.status"
                    :modifiers="['narrow']"
                  />
                </template>

                <div
                  v-if="issue?.kumba_send_to_sap_notice"
                  class="form__group-message form__group-message--narrow typo-small"
                >
                  {{ issue.kumba_send_to_sap_notice }}
                </div>
                <!-- TODO: hide when SAP details are pending or exist -->
                <div
                  v-if="!(!issue.policy?.send_to_sap && issue?.kumba_issue_notification)"
                  class="issue__sap issue__sap--no-margin-bottom issue__sap--no-border"
                >
                  <FormSubmitButton
                    :buttonLabel="$t('regions.sap.send_to_sap')"
                    class="button button--inline button--full-width"
                    :disabled="!issue.policy?.send_to_sap"
                    :isSaving="sendToSapLoading"
                    type="button"
                    :isInverse="false"
                    @click="sendToSap"
                  />
                </div>

              </div>
            </div>

            <div v-if="issueId" class="issue-ratings__section">
              <div class="issue-inline-field issue-inline-field--heading">
                <p class="issue-inline-field__title">
                  {{ $t('issues.details') }}
                </p>
              </div>
              <div class="form__group-inner">

                <!-- Issue Number -->
                <FormGroup
                  :label="$t('issues.issue_number')"
                  :value="shortId"
                  :modifiers="['narrow', 'id']"
                />

                <!-- Reporter -->
                <FormGroup
                  :label="$t('issues.reporter')"
                  :value="issue.inspected_by.full_name"
                  :modifiers="['narrow']"
                />

                <!-- Captured At -->
                <FormGroup
                  :label="$t('issues.captured')"
                  :value="issue?.inspected_at ? formatDate(issue.inspected_at) : ''"
                  :modifiers="['narrow']"
                />

                <div
                  v-if="issueId && downloadIssuePath"
                  class="issue__download issue__download--no-margin-bottom issue__download--no-border"
                >
                  <a
                    class="button button--inline button--full-width"
                    :href="downloadIssuePath"
                    target="_blank"
                  >
                    {{ $t('general.download') }}
                  </a>
                </div>
              </div>
            </div>

            <!-- History -->
            <div
              v-if="issueId && issue.total_modifications_count"
              class="issue-ratings__section"
            >
              <div class="issue-inline-field issue-inline-field--heading">
                <div class="issue-inline-field__title">
                  {{
                    pluralize(
                      issue.total_modifications_count,
                      $t('issues.modification'),
                      $t('issues.modifications')
                    )
                  }}
                </div>
                <div class="issue-inline-field__field">
                  <router-link
                    class="button button-small"
                    :to="{
                      name: historyRouteName,
                      params: { inspectionId: issue.inspection_id }
                    }"
                  >
                    {{ $t('issues.view_history') }}
                  </router-link>
                </div>
              </div>
            </div>
            <div
              v-if="!issueId"
              class="issue-ratings__section issue-ratings__section--large-gap"
            >
              <IssueRiskRating v-if="showRisk" :issue="issue" />
            </div>
          </template>
          <div
            v-if="issueId && issue?.policy?.destroy"
            class="issue__delete"
          >
            <button
              class="button button--inline button--full-width button--danger"
              type="button"
              @click="setDialogTypeAndId('issue', issueId)"
            >
              {{ $t('general.delete_issue') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </form>
  <Dialog
    v-if="dialogIsOpen"
    :message="dialogData.text.message"
    :title="dialogData.text.title"
    @dismiss-dialog="dismissDialogAction"
  >
    <template #actions>
      <button
        aria-label="delete"
        class="button button--inverse"
        type="button"
        @click="dialogData.action"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import {
  onMounted,
  ref,
  reactive,
  onBeforeUnmount,
  computed,
  inject,
  nextTick,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import I18n from 'i18n-js';
import { v4 as uuid } from 'uuid';

// Utils
import { acceptedFileTypes } from '@/config/accepted_file_types';
import pluralize from '@/functions/pluralize';
import { renderShortId } from '@/functions/utils';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import ratingToPriority from '@/functions/simm/rating_to_priority';
import getElementMaterials from '@/functions/simm/get_element_materials';
import { byCreatedAt } from '@/functions/order_by';
import { isoDate, formatDate } from '@/functions/date_format_helpers';
import sliceObject from '@/functions/slice_object';
import {
  rgeoToLatLng,
  geoJSONPolygonToLatLngs,
  rgeoPolygonToGeoJSON,
} from '@/functions/geospatial_conversions';
import regionPolygonOptions from '@/functions/region_polygon_options';
import { useShip, useOperation, useEquipment } from '@/functions/use_remote_object';
import { useIssuesStore } from '@/stores/issues/issuesStore';
import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';
import DETERIORATION_MECHANISMS, {
  DEFAULT_RATINGS,
} from '@/functions/simm/deterioriation_mechanisms';
import useOrganisationProfile from '@/functions/use_organisation_profile'; 
import useUploader from '@/vendor/use_uploader';

// Components
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import Checkbox from '@/components/form/checkbox.vue';
import DescriptionSelect from '@/components/form/description_select.vue';
import DescriptionSelectDropdown from '@/components/form/description_select_dropdown.vue';
import Select from '@/components/form/select.vue';
import IssueEditField from '@/components/issues/issue_edit_field.vue';
import IssueTask from '@/components/issues/issue_task.vue';
import IssueTaskFormModal from '@/components/issues/issue_task_form_modal.vue';
import IssueRiskRating from '@/components/issues/issue_risk_rating.vue';
import IssueNav from '@/components/issues/issue_nav.vue';
import IssueCarousel from '@/components/issues/issue_carousel';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import Dialog from '@/components/generic/dialog.vue';
import ImageViewer from '@/components/image_viewer.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import AttachmentUploader from '@/components/generic/attachment_uploader.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FormGroup from '@/components/form/form_group.vue';
import MapPreview from '@/components/generic/map_preview.vue';
import LeafletMap from '@/vendor/components/leaflet_map.vue';

// Assets
import Plus16Svg from '@/images/plus-16.svg';
import AiIconSvg from '@/images/ai-icon.svg';
import WrenchIcon from '@/images/wrench-icon.svg';
import ExclamationTriangleSvg from '@/images/exclamation-triangle.svg';
import { DotIcon } from '@/vendor/leaflet_mods';

const PointIcon = DotIcon.extend({
  options: {
    className: 'map-modal__point',
    iconSize: [9, 9],
    iconAnchor: [5, 5],
  },
});

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: false, default: null },
  equipmentId: { type: String, required: false, default: null },
  shipId: { type: String, required: false, default: null },
  inspectionId: { type: String, required: true },
  issueId: { type: String, default: null },
  /** Use a specific route for the header nav back button */
  issueNavBackRoute: { type: String, required: false, default: null },
});

const issueStore = useIssuesStore();
const issueLocationStore = useIssueLocationStore();

const route = useRoute();
const router = useRouter();
const ajax = useAjax();
const emitter = useEmitter();
const currentUser = inject('currentUser');
const currentOrganisation = inject('currentOrganisation');
const uploader = useUploader();
const initialInspectionDate = ref(null);
const initialInspectionStatus = ref(null);

let lastGoodIssue;
const loading = ref(true);
const initialIssue = ref(null);
const issue = reactive(createNewIssue());
const imagesContainer = ref(null);
const viewingImage = ref(null);
const shortId = computed(() => props.issueId ? renderShortId(props.issueId) : null);
const errors = ref(null);
const imageValid = ref(true);
const imageValidationMessage = ref(null);

const newRatingOpen = ref(false);
const newRating = ref(null);
const newTask = ref(null);

const errorSavingTask = ref(null);
const toggleIssueModal = ref(null);
const dialogIsOpen = ref(false);
const deletionId = ref(null);
const currentDialogType = ref(null);

let marker = null;
const map = ref(null);
const loadMap = ref(false);
const regions = ref(null);
const saving = ref(false);
const textTitle = ref();
const textDescription = ref();
const textRecommendation = ref();
const textLocationDescription = ref();
const issueEditFieldInputs = [
  textTitle,
  textDescription,
  textRecommendation,
  textLocationDescription
];

const sendToSapLoading = ref(false);
const operationType = ref(getType());
const currentOperation = ref(getOperation());
let sapInformationList = ['asset_name', 'functional_location', 'equipment_number', 'work_centre', 'planner_group', 'destination_site'];
let sapNotificationList = ['notification_reference', 'work_order_reference', 'work_order_status'];

const { showRisk, isAga } = useOrganisationProfile();

const emptyText = computed(() => {
  return I18n.t('issues.no_location');
});

const locationRouteName = computed(() => {
  const routes = {
    isOverlay: null,
    issueLocationPage: null,
    locationPage: null,
  }

  const operationRoutes = {
    plants: plantRoutes(routes, operationType.value),
    ships: shipRoutes(routes, operationType.value),
    equipment: equipmentRoutes(routes, operationType.value),
  }

  const newRoutes = operationType.value ? operationRoutes[operationType.value] : '';

  return newRoutes.isOverlay ? newRoutes.issueLocationPage : newRoutes.locationPage;
});

function getType() {
  if (props.shipId) {
    return 'ships';
  } else if (props.equipmentId) {
    return 'equipment';
  }
  return 'plants';
}

function getOperation() {
  switch (true) {
    case operationType.value === 'ships':
      return useShip(props);

    case operationType.value === 'equipment':
      return useEquipment(props);

    default:
      return useOperation(props);
  }
}

function plantRoutes(routes, type) {
  if (type !== 'plants') return;

  routes.isOverlay = ['regionsIssue'].includes(route.name);
  routes.issueLocationPage = setLinkData('issuesLocationTypePlant');
  routes.locationPage = route.params.issueId ? setLinkData('issueLocationPlant') : setLinkData('newIssueLocationPlant');

  if (issue?.location_type === 'Plants::HeavyMobileEquipmentLocation') {
    routes.isOverlay = ['regionsIssuesTypeHme'].includes(route.name);
    routes.issueLocationPage = setLinkData('regionsLocationTypeHme');
    if (route.params.issueId && !currentOperation.value?.policy?.heavy_mobile_equipment_enabled) {
      routes.locationPage = setLinkData('issue');
    } else {
      const hmeId = issue?.location?.heavy_mobile_equipment_id;
      routes.locationPage = route.params.issueId ? setLinkData('issueLocationHme', hmeId) : setLinkData('newIssueLocationPlant');
    }
  }

  return routes;
}

function shipRoutes(routes, type) {
  if (type !== 'ships') return;

  routes.isOverlay = ['shipRegionsIssue'].includes(route.name);
  routes.issueLocationPage = setLinkData('shipIssuesLocationTypeShip');
  routes.locationPage = route.params.issueId ? setLinkData('shipIssueLocationShip') : setLinkData('shipNewIssueLocationShip');

  return routes;
}

function equipmentRoutes(routes, type) {
  if (type !== 'equipment') return;

  routes.isOverlay = ['equipmentRegionsIssue'].includes(route.name);
  routes.issueLocationPage = setLinkData('equipmentIssuesLocationTypeEquipment');
  routes.locationPage = route.params.issueId ? setLinkData('equipmentIssueLocationEquipment') : setLinkData('equipmentNewIssueLocationEquipment');

  return routes;
}

function setLinkData(name, heavyMobileEquipmentId) {
  return heavyMobileEquipmentId ? { name, params: { heavyMobileEquipmentId } } : { name };
}

const historyRouteName = computed(() => {
  const operationRouteName = {
    plants: 'issueHistory',
    ships: 'shipIssueHistory',
    equipment: 'equipmentIssueHistory',
  }

  return operationType.value ? operationRouteName[operationType.value] : '';
});

const operationData = computed(() => {
  if (route.params.shipId) {
    const baseUrl = `/organisations/${props.organisationId}/ships/${route.params.shipId}`;
    const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
    return {
      name:'ships',
      operation: currentOperation.diagram_identifier,
      inspection: 'shipInspection',
      history: 'shipIssueHistory',
      regionsUrl: `${baseUrl}/regions`,
      inspectionUrl: inspectionUrl,
      issuesUrl: `${inspectionUrl}/issues`,
      issueUrl: `${inspectionUrl}/issues/${props.issueId}`
    };
  } else if (route.params.equipmentId) {
    const baseUrl = `/organisations/${props.organisationId}/equipment/${route.params.equipmentId}`;
    const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
    return {
      name:'equipment',
      operation: currentOperation.diagram_identifier,
      inspection: 'equipmentInspection',
      history: 'equipmentIssueHistory',
      regionsUrl: `${baseUrl}/regions`,
      inspectionUrl: inspectionUrl,
      issuesUrl: `${inspectionUrl}/issues`,
      issueUrl: `${inspectionUrl}/issues/${props.issueId}`
    };
  } else {
    const baseUrl = `/organisations/${props.organisationId}/operations/${route.params.operationId}`;
    const inspectionUrl = `${baseUrl}/plants/inspections/${props.inspectionId}`;
    const hmeRegionsUrl = `${baseUrl}/plants/heavy_mobile_equipments/${issue?.location?.heavy_mobile_equipment_id}/heavy_mobile_equipment_regions`;
    return {
      name:'plants',
      operation: currentOperation.bounds,
      inspection: 'inspection',
      history: 'issueHistory',
      regionsUrl: `${baseUrl}/regions`,
      hmeRegionsUrl: hmeRegionsUrl,
      inspectionUrl: inspectionUrl,
      issuesUrl: `${inspectionUrl}/issues`,
      issueUrl:`${inspectionUrl}/issues/${props.issueId}`,
      hmeLink: `${baseUrl}/plants/heavy_mobile_equipments`,
    };
  }
});

const deteriorationMechanisms = computed(() => {
  if (!issue.element_material) return {};

  let dms = { ...DETERIORATION_MECHANISMS[issue.element_material] };
  issue.deterioration_mechanism_ratings.forEach((r) => delete dms[r.name]);

  return dms;
});

const sortedImages = computed(() => {
  return issue.issue_images.filter((x) => x.attached).sort(byCreatedAt);
});

const canEdit = computed(() => {
  if (props.issueId) return initialIssue.value?.policy?.edit || false;
  return true;
});

const canResolve = computed(() => {
  if (!props.issueId) return true;

  const policy = initialIssue.value?.policy;
  return policy?.resolve ?? policy?.edit ?? false;
});

const downloadIssuePath = computed(() => {
  return issue.download_url;
});

/**
 * Returns the correct text and action for the dialog.
 */
const dialogData = computed(() => {
  switch (currentDialogType.value) {
    case 'image':
      return {
        text: dialogTextObject('image'),
        action: removeImage,
      };
    case 'issue':
      return {
        text: dialogTextObject('issue'),
        action: deleteIssue,
      };
    default:
      return {
        text: dialogTextObject('task'),
        action: deleteTask,
      };
  }
});

/**
 * Finds the accepted det mech image
 */
const acceptedImage = computed(() => {
  return issue.issue_images.find((image) => {
    return image.accepted === true;
  });
});

function hasAcceptedImage(rating) {
  return isSteelDamageRating(rating) && acceptedImage.value;
}

const statusOptions = [
  { label: I18n.t('issues.open_issue'), value: 'open_issue' },
  { label: I18n.t('issues.resolved'), value: 'resolved' },
];

function priorityLabel(priority) {
  if (currentOrganisation.value?.aga_profile) {
    return I18n.t(`aga_priority_labels.${priority}`);
  } else {
    return I18n.t(`priority_labels.${priority}`);
  }
}

const elementClassOptions = [
  'primary',
  'secondary',
  'tertiary',
  'non_structural',
].map((value) => ({
  label: I18n.t(`element_classes.${value}.name`),
  value,
  description: [I18n.t(`element_classes.${value}.description`)],
  disabled: false,
}));
const overallConditionOptions = DEFAULT_RATINGS.map((rating) => ({
  label: rating.toString(),
  value: rating,
  description: I18n.t(`condition_descriptions.${rating}`),
  iconClass: `issue-ratings__indicator issue-ratings__indicator--condition-${rating}`,
}));
const repairPriorityOptions = computed(() => ['none', 'M', '3', '2', '1', 'X'].map(
  (priority) => ({
    label: priority === 'none' ? '-' : priorityLabel(priority),
    value: priority,
    description: I18n.t(`priority_descriptions.${priority}`),
    iconClass: `issue-ratings__indicator issue-ratings__indicator--priority-${priority}`,
  })
));

onMounted(async () => {
  await loadInitialIssue();
  await loadLocation();
  await loadRegions();
  await loadInspection();
  await nextTick(() => {
    initCarousel();
    loadMap.value = true
  });
});

onBeforeUnmount(destroyCarousel);

watch(
  () => props.issueId,
  async () => {
    destroyCarousel();
    await loadInitialIssue();
    nextTick(initCarousel);
  }
);

watch(
  () => issue,
  (newValue) => {
    issueLocationStore.$patch({ formData: newValue });
  }, { deep: true }
);

/**
 * Disable the location button if the location we are editing
 * is Hme and heavy_mobile_equipment_enabled is false
 */
const disableLocation = computed(() => {
  const isHme = issue?.location_type === 'Plants::HeavyMobileEquipmentLocation';
  const disableStatus = issue?.id && isHme && !currentOperation.value?.policy?.heavy_mobile_equipment_enabled;
  return disableStatus || !canEdit.value;
});

/**
 * Provide element material options for selection.
 */
const elementMaterialOptions = computed(() => {
  const element_materials = getElementMaterials(setLocationType())

  return element_materials.filter((element) => {
    return element !== 'timber' || props?.operationId;
  }).map((value) => ({
    label: I18n.t(`element_materials.${value}`),
    value,
  }));
});

/**
 * Sets the correct scope.
 * Passed into the Issue Nav component
 */
const inspectionScope = computed(() => {
  return initialInspectionStatus.value === 'approved' ? 'approved_inspections' : 'inspections';
});

// Checks if a specific route was passed in via props
const hasIssueNavBackRoute = computed(() => {
  return props.issueNavBackRoute !== null;
});

// Default back route - to inspections
const defaultNavBackRoute = computed(() => {
  return {
    name: operationData.value.inspection,
    params: { inspectionId: props.inspectionId, scope: inspectionScope.value },
  };
});

// Set back route based on props
const setNavRouterPath = computed(() => {
  return hasIssueNavBackRoute.value ? { name: props.issueNavBackRoute } : defaultNavBackRoute.value;
});

// Set route path status based on props and history
const setRouterPathStatus = computed(() => {
  return hasIssueNavBackRoute.value || useRouterPath(router?.options?.history?.state?.back);
});

function createNewIssue(inspectionDate) {
  return {
    title: '',
    description: '',
    recommendation: '',
    location_description: '',
    location: { location: null },
    overall_condition_rating: null,
    element_material: null,
    element_class: null,
    priority: null,
    risk_rating: null,
    closed_at: null,
    requires_task: false,
    tasks_close_issue: false,
    status: 'open_issue',
    issue_documents: [],
    issue_images: [],
    deterioration_mechanism_ratings: [],
    tasks: [],
    inspection: { date: inspectionDate },
    download_url: null,
    direct_region: { name: null },
    policy: { create: true }
  };
}

let imageCarousel = null;
function destroyCarousel() {
  if (imageCarousel) {
    imageCarousel.destroy();
  }
}

function initCarousel() {
  imageCarousel = new IssueCarousel(
    imagesContainer.value,
    operationData.value.issueUrl,
    sortedImages.value,
    image => {
      /**
       * If the image thumbnail is clicked it preselects
       * the Description tab when damage calculator opens
       */
      router.replace({ query: { tab: 'description' } });
      viewingImage.value = image;
    },
  );
}

function loadLocation() {
  if (Object.hasOwn(initialIssue.value, 'id') && initialIssue.value?.location?.location) {
    issueLocationStore.$patch({
      location: {
        location: initialIssue.value.location.location
      }
    });
  } else if (issueLocationStore?.location?.location) {
    issue.location.location = issueLocationStore.location.location;
  }
}

/**
 * Update the currently selected image details in the issue_images
 * array on the current issue
 */
function updateCurrentImage(image) {
  const index = findSelectedImageIndex(image);

  if (image?.id) {
    issue.issue_images[index].description = image?.description;
  }
}

/**
 * If an image has been saved, use the blob id to find an image
 * in the issue images. This is because new images have dummy id's.
 * When an issue has not yet been saved, the dummy id's are fine to use.
 */
function findSelectedImageIndex(selectedImage) {
  return issue.issue_images.findIndex((image) => {
    if (image['issue_id']) {
      return image['image_blob']['id'] === selectedImage['image_blob']['id'] || image['id'] === selectedImage['id'];
    } else {
      return image['id'] === selectedImage['id'];
    }
  });
}

/**
 * Update the currently selected image annotation details in the issue_images
 * array on the current issue
 */
function annotateImage(image) {
  const index = findAnnotatedImageIndex(image);

  issue.issue_images[index].isNew = image?.isNew;
  issue.issue_images[index].description = image?.description;
  issue.issue_images[index].image_blob = image?.image_blob;
  issue.issue_images[index].original_image_blob ??= image?.original_image_blob;
  issue.issue_images[index].upload = image?.upload;
  issue.issue_images[index].original_upload ??= image?.original_upload;
  issue.issue_images[index].original_url ??= image?.original_url;
  issue.issue_images[index].url = image?.url;
  issue.issue_images[index].markup = image?.markup;
  updateCarousel();
}

/**
 * All annotated images should have id's at the point this is required
 */
function findAnnotatedImageIndex(selectedImage) {
  return issue.issue_images.findIndex((image) => {
    return image['id'] === selectedImage['id'];
  });
}

async function loadInspection() {
  let { ok, data } = await ajax(operationData.value.inspectionUrl);

  if (ok) {
    initialInspectionStatus.value = data.status;
    initialInspectionDate.value = data.date;
  }
}

async function loadInitialIssue() {
  loading.value = true;

  if (issueLocationStore.formData) {
    Object.assign(issue, issueLocationStore.formData);
  }

  if (props.issueId) {
    initialIssue.value = null;
    let { ok, data } = await ajax(operationData.value.issueUrl);

    if (ok) {
      issue.direct_region = null;
      initialIssue.value = null;
      initialIssue.value = data;
      issueStore.updateIssue(data);
      lastGoodIssue = convertIncomingIssueData(data);
      Object.assign(issue, lastGoodIssue);
      loadHme(data)
    }
  } else {
    await loadInspection();
    initialIssue.value = createNewIssue(initialInspectionDate.value);
  }
  loading.value = false;
}

async function loadHme(issueData) {
  if (issueData.location_type === 'Plants::HeavyMobileEquipmentLocation') {
    let { ok, data } = await ajax(`${operationData.value.hmeLink}/${issueData.location.heavy_mobile_equipment_id}`);

    if (ok) {
      issue.location['diagram_identifier'] = data.diagram_identifier;
    }
  }
}

function convertIncomingIssueData(data) {
  let converted = {
    ...data,
    inspected_at: new Date(data.inspection.inspected_at),
  };
  if (data.tasks) {
    data.tasks = data.tasks.map((task) => ({
      ...task,
      due_date: task.due_date ? new Date(task.due_date) : null,
      closed_at: task.closed_at ? new Date(task.closed_at) : null,
    }));
  }
  return converted;
}

function updateFileList(file, type) {
  if (type === 'image') {
    issue.issue_images.push(file);
    updateCarousel(sortedImages.value.length - 1);
  }
}

async function saveBlob(file, type) {
  if (!props.issueId) return;
  const blobData = await uploadFile(file, type);
  const fileObject = type === 'image' ?
  { issue_images_attributes: [{ image: blobData.signed_id }] } :
  { issue_documents_attributes: [{ file: blobData.signed_id }] };
  updateIssue(fileObject);
}

function wrongFileType(imageData) {
  const isEmpty = Object.keys(imageData).length <= 0;
  imageValid.value = isEmpty;
  imageValidationMessage.value = !isEmpty ? I18n.t('general.image_validation_message', imageData) : null;
}

async function removeAttachmentItem(id) {
  if (!props.issueId) {
    issue.issue_documents = issue.issue_documents.filter(attachment => attachment.id !== id);
  } else {
    let { ok } = await ajax(operationData.value.issueUrl, {
      method: 'PATCH',
      body: { issue: { issue_documents_attributes: [{ id: id, _destroy: true }] } }
    });

    if (ok) {
      issue.issue_documents = issue.issue_documents.filter(attachment => attachment.id !== id);
    }
  }
  dismissDialogAction();
}

function updateCarousel(...args) {
  imageCarousel.update(sortedImages.value, ...args);
}

async function applyDamageRating(damageRating, values) {
  if (issue.element_material && issue.element_material !== 'steel') {
    return;
  }

  issue.element_material ||= 'steel';

  const index = findSelectedImageIndex(viewingImage.value);
  const structuralDamage = issue.deterioration_mechanism_ratings.find((rating) => {
    return rating['name'] === 'structural_damage';
  });

  clearAcceptedImageLocally();

  if (props.issueId) {
    const imageAttributes = clearedAcceptedImageAttributes();
    const acceptedIndex = imageAttributes.findIndex((image) => image.id === viewingImage.value.id);
    imageAttributes[acceptedIndex] = {
      id: viewingImage.value.id,
      ...values,
      accepted: true
    };
    await updateIssue({
      issue_images_attributes: imageAttributes
    });
  } else {
    issue.issue_images[index] = {
      ...issue.issue_images[index],
      ...values,
      accepted: true,
    };
  }

  if (structuralDamage) {
    await updateRating(structuralDamage, damageRating, false);
  } else {
    createNewRating('structural_damage', deteriorationMechanisms.value[damageRating]);
    await addRating(damageRating);
  }
}

function currentAcceptedImageIndex() {
  return issue.issue_images.findIndex((image) => {
    return image.accepted === true;
  });
}

function clearAcceptedImageLocally() {
  const index = currentAcceptedImageIndex();

  if (index < 0) return;

  issue.issue_images[index].accepted = false;
}

function clearedAcceptedImageAttributes() {
  return issue.issue_images.map((image) => {
    return {
      id: image.id,
      accepted: false,
    };
  });
}

async function clearDamageRating() {
  const index = findSelectedImageIndex(viewingImage.value);

  const valuesToClear = {
    calculation_type: null,
    damage_marker_markup: {},
    damaged_structure: null,
    deviation: null,
    deviation_unit: null,
    thickness: null,
    thickness_unit: null,
    accepted: false,
  }

  if (props.issueId) {
    await updateImage(issue.issue_images[index], {
      ...valuesToClear,
    });
  } else {
    issue.issue_images[index] = {
      ...issue.issue_images[index],
      ...valuesToClear,
    };
  }
}

async function updateImage(image, value) {
  await updateIssue({
    issue_images_attributes: [{ id: image.id, ...value }],
  });
}

function createNewRating(name, detMech) {
  newRating.value = { name, ...detMech };
  window.dispatchEvent(new Event('resize')); // HACK: force popper to reposition
}

function cancelNewRating() {
  newRating.value = null;
  newRatingOpen.value = false;
}

async function addRating(rating) {
  issue.deterioration_mechanism_ratings.push({
    id: uuid(),
    name: newRating.value.name,
    value: rating,
  });
  updateConditionRating();

  validateInput('overall_condition_rating');

  await updateIssue({
    deterioration_mechanism_ratings_attributes: [
      { name: newRating.value.name, value: rating },
    ],
  });

  newRating.value = null;
  newRatingOpen.value = false;
}

function ratingItems(detMech) {
  let enabledRatings =
    DETERIORATION_MECHANISMS[issue.element_material][detMech.name].ratings;
  return DEFAULT_RATINGS.map((rating) => ({
    value: rating,
    label: rating.toString(),
    description: I18n.t(
      `dmr_descriptions.${issue.element_material}.${detMech.name}.${rating}`
    ),
    disabled: !enabledRatings.includes(rating),
    iconClass: `issue-ratings__indicator issue-ratings__indicator--condition-${rating}`,
  }));
}

async function updateRating(rating, value, clearAcceptedImages = true) {
  rating.value = value;
  updateConditionRating();

  let updates = {
    deterioration_mechanism_ratings_attributes: [{ id: rating.id, value }],
  };

  if (isSteelDamageRating(rating) && clearAcceptedImages && currentAcceptedImageIndex() >= 0) {
    clearAcceptedImageLocally();
    updates['issue_images_attributes'] = clearedAcceptedImageAttributes();
  }

  await updateIssue(updates);
}

async function removeRating(rating) {
  issue.deterioration_mechanism_ratings = issue.deterioration_mechanism_ratings.filter(
    (x) => x.id !== rating.id
  );
  updateConditionRating();

  let updates = {
    deterioration_mechanism_ratings_attributes: [
      { id: rating.id, _destroy: true },
    ],
  };

  if (isSteelDamageRating(rating) && currentAcceptedImageIndex() >= 0) {
    clearAcceptedImageLocally();
    updates['issue_images_attributes'] = clearedAcceptedImageAttributes();
  }

  await updateIssue(updates);
}

function isSteelDamageRating(rating) {
  return issue?.element_material === 'steel' && rating.name === 'structural_damage';
}


function loadIssue(data) {
  issueStore.updateIssue(data);
  lastGoodIssue = convertIncomingIssueData(data);
  Object.assign(issue, createNewIssue()); // Reset the issue
  Object.assign(issue, lastGoodIssue);
  updateCarousel();
}

function revertIssue() {
  Object.assign(issue, lastGoodIssue);
  updateCarousel();
}

async function updateIssue(updates) {
  if (!props.issueId) return;
  const isTask = !!updates?.tasks_attributes;
  // Reset the previous possible error that might have occurred for the buttons to stop loading
  errorSavingTask.value = null;
  toggleIssueModal.value = null;

  let { ok, data } = await ajax(operationData.value.issueUrl, {
    method: 'PATCH',
    body: { issue: updates },
  });

  if (ok) {
    // Closes the task modal when saving is complete
    if (isTask) {
      newTask.value = null;
      toggleIssueModal.value = false;
    }
    loadIssue(data);
    return true;
  } else {
    // Notifies the task modal that an error occurred and the buttons should be enabled
    isTask && (errorSavingTask.value = true);
    revertIssue();
    return false;
  }
}

async function update(field, value) {
  issue[field] = value;
  if (field === 'overall_condition_rating' || (field === 'element_class' && isAga.value)) updatePriority();
  await updateIssue({ [field]: value });

  validateInput(field);
}

async function updateElementMaterial(newMaterial) {
  issue.element_material = newMaterial;
  issue.deterioration_mechanism_ratings = [];
  if (props.issueId) updateConditionRating();
  await updateIssue({ element_material: newMaterial });

  validateInput('element_material');
}

function updateConditionRating() {
  let allRatings = issue.deterioration_mechanism_ratings.map(
    (x) => x.value
  );
  issue.overall_condition_rating = Math.max(0, ...allRatings);
  updatePriority();
}

function updatePriority() {
  issue.priority = ratingToPriority(issue.overall_condition_rating, { elementClass: issue.element_class, isAga: isAga.value });

  validateInput('priority');
}

function createNewTask() {
  newTask.value = {
    isNew: true,
    id: uuid(),
    labels: [],
    status: 'incomplete',
  };
}

async function formatTaskForPost(task) {
  return {
    ...sliceObject(task, [
      'description',
      'external_reference',
      'task_type',
      'status',
      'labels',
    ]),
    due_date: isoDate(task.due_date),
    task_documents_attributes: await Promise.all(
      task.task_documents.map(formatDocumentForPost)
    ),
  };
}

async function addNewTask(task) {
  issue.tasks = [...issue.tasks, task];
  if (props.issueId) {
    updateIssue({
      tasks_attributes: [
        {
          ...(await formatTaskForPost(task)),
          status: undefined,
        },
      ],
    });
  } else {
    errorSavingTask.value = false;
    newTask.value = null;
    toggleIssueModal.value = false;
  }
}

async function updateTask(task, updatedTask) {
  // check if any docs removed
  const deletedDocuments = task.task_documents.filter((id) => {
    return !updatedTask.task_documents.includes(id);
  });

  // remove docs
  if (deletedDocuments) {
    deletedDocuments.forEach((document) => {
      updateIssue({
        tasks_attributes: [
          { id: task.id, task_documents_attributes: [{ id: document.id, _destroy: true }] },
        ],
      });
    });
  }

  Object.assign(task, updatedTask);
  if (props.issueId) {
    updateIssue({
      tasks_attributes: [
        { id: task.id, ...(await formatTaskForPost(task)), status: undefined },
      ],
    });
  }
}

function markTaskAs(task, status) {
  task.status = status;
  if (status !== 'incomplete') {
    task.closed_at = new Date();
    task.closed_by = currentUser;
  }
  if (props.issueId) {
    updateIssue({ tasks_attributes: [{ id: task.id, status }] });
  }
}

function deleteTask() {
  issue.tasks = issue.tasks.filter((x) => x.id !== deletionId.value);
  if (props.issueId) {
    updateIssue({ tasks_attributes: [{ id: deletionId.value, _destroy: true }] });
  }

  dismissDialogAction();
}

async function deleteIssue() {
  let { ok } = await ajax(operationData.value.issueUrl, { method: 'DELETE' });

  if (ok) {
    router.back();
    dismissDialogAction();
  }
}

async function onMapInit(locationData) {
  if (regions.value) {
    regions.value.forEach(loadRegion);
  }

  if (locationData) {
    let location = rgeoToLatLng(locationData);
    setMarker(...location);
    map.value.goTo(location, 18);
  } else if (!screenAtLeast('medium')) {
    let { lat, lng } = map.value.getCenter();
    setMarker(lat, lng);
  }
}

function loadRegion(region) {
  let geoJSON = rgeoPolygonToGeoJSON(region.bounds);
  let latLngs = geoJSONPolygonToLatLngs(geoJSON);
  let polygon = map.value?.addPolygon(
    latLngs,
    regionPolygonOptions(region.region_type)
  );
  let regionObject = {
    ...region,
    geoJSON,
    latLngs,
    polygon,
  };
  polygon.region = regionObject;
}

function setMarker(lat, lng) {
  map.value.enableCrosshair(lat, lng);
  if (!marker) {
    marker = map.value.addMarker([lat, lng], { icon: new PointIcon() });
  } else {
    marker.setLatLng([lat, lng]);
  }
}

async function formatIssueImageForPost(image) {
  if (!image?.isNew) return;

  // means image was already uploaded, now needs original uploaded
  if (image?.original_upload) {
    await uploadFile(image, 'original_image');
  }
  await uploadFile(image, 'image');

  return {
    image: image.image_blob.signed_id,
    original_image: image?.original_image_blob?.signed_id,
    description: image.description,
    markup: image?.markup,
    damage_marker_markup: image?.damage_marker_markup,
    calculation_type: image.calculation_type,
    damaged_structure: image.damaged_structure,
    thickness: image?.thickness,
    thickness_unit: image?.thickness_unit,
    deviation: image?.deviation,
    deviation_unit: image?.deviation_unit,
    accepted: image?.accepted,
  };
}

async function formatDocumentForPost(file) {
  if (!file?.isNew) return;
  await uploadFile(file, 'attachment');
  return { file: file.file_blob.signed_id };
}

async function uploadFile(file, type) {
  const upload = type !== 'original_image' ? file.upload : file.original_upload;
  const blob = await uploader.upload(upload);
  if (type === 'image') {
    Object.assign(file.image_blob, blob);
  } else if (type === 'original_image') {
    Object.assign(file.original_image_blob, blob);
  } else if (type === 'attachment') {
    Object.assign(file.file_blob, blob);
  }
  return blob;
}

function formatDMRForPost(dmr) {
  return sliceObject(dmr, ['name', 'value']);
}

async function formatIssueForPost() {
  return {
    ...sliceObject(issue, [
      'title',
      'description',
      'recommendation',
      'location_description',
      'overall_condition_rating',
      'priority',
      'element_material',
      'element_class',
      'requires_task',
      'tasks_close_issue',
    ]),
    location_type: setLocationType(),
    location_attributes: setLocation(),
    tasks_attributes: await Promise.all(issue.tasks.map(formatTaskForPost)),
    issue_images_attributes: await Promise.all(
      issue.issue_images.map(formatIssueImageForPost)
    ),
    issue_documents_attributes: await Promise.all(
      issue.issue_documents.map(formatDocumentForPost)
    ),
    deterioration_mechanism_ratings_attributes: issue.deterioration_mechanism_ratings.map(
      formatDMRForPost
    ),
  };
}

function setLocationType() {
  const operationLocationType = {
    plants: issueLocationStore?.location?.type === 'plant' ? 'Plants::PlantLocation' : 'Plants::HeavyMobileEquipmentLocation',
    ships: 'Ships::ShipLocation',
    equipment: 'Equipments::EquipmentLocation',
  }

  return operationType.value ? operationLocationType[operationType.value] : '';
}

function setLocation() {
  const location = issueLocationStore?.location?.location;
  if (issueLocationStore?.location?.type === 'heavy_mobile_equipment') {
    return location ? {
      location: location,
      heavy_mobile_equipment_id: issueLocationStore?.location?.heavy_mobile_equipment_id,
      direct_region_id: issueLocationStore?.location?.direct_region_id,
    } : {};
  }

  const operationLocation = {
    plants: { operation_id: props.operationId },
    ships: { ship_id: props.shipId },
    equipment: { equipment_id: props.equipmentId },
  }

  const operationData = operationType.value ? operationLocation[operationType.value] : '';
  return location ? { location, ...operationData } : {};
}

async function saveNewIssue() {
  saving.value = true;

  let { ok, data } = await ajax(`${operationData.value.issuesUrl}`, {
    method: 'POST',
    body: { issue: await formatIssueForPost() },
  });

  if (ok) {
    errors.value = null;

    if (issueLocationStore.saveAndCreateNewIssue) {
      await nextTick();
      Object.assign(issue, createNewIssue());
      updateCarousel();
      emitter.emit('toast', {
        status: 'success',
        message: I18n.t('issues.new_issue_success_toast'),
      });
      issue.location.location = null;
      issueLocationStore.location = null;
      issueLocationStore.formData = null;
      await nextTick();
      issueEditFieldInputs.forEach(input => input.value.internalValue = '');
    } else {
      issueLocationStore.$reset();
      router.replace({
        name: operationData.value.inspection,
        params: { inspectionId: props.inspectionId, scope: 'inspections' },
      });
    }
  } else {
    errors.value = data;
  }

  saving.value = false;
}

function hasError(field) {
  return errors.value && errors.value?.issue && !!errors.value?.issue[field];
}

function validateInput(field) {
  if (errors.value) {
    errors.value.issue[field] = null;
  }
}

/**
 * Toggles dialog,
 * stores the id used for deletion,
 * stores the type of dialog. eg. image, file, task
 */
function setDialogTypeAndId(type, id) {
  dialogIsOpen.value = true;
  deletionId.value = id;
  currentDialogType.value = type;
}

/**
 * Toggles dialog for removing an image,
 */
function confirmDeleteDialog(type, id) {
  setDialogTypeAndId(type, id);
}

/**
 * Sets the correct text for the dialog message based off the type.
 */
function dialogTextObject(type) {
  return {
    title: I18n.t('issues.confirm_delete_title', { type: I18n.t(`issues.type_${type}`) }),
    message: type === 'issue' ? I18n.t('issues.confirm_issue_delete_message') : I18n.t('issues.confirm_delete_message'),
  };
}

/**
 * Closes and clears any stored values
 */
function dismissDialogAction() {
  dialogIsOpen.value = false;
  deletionId.value = null;
}

async function removeImage() {
  if (!props.issueId) {
      issue.issue_images.splice(issue.issue_images.findIndex((image) => {
        return image.id === deletionId.value;
      }), 1);
  } else {
    let { ok } = await ajax(operationData.value.issueUrl, {
      method: 'PATCH',
      body: {
        issue: { issue_images_attributes: [{ id: deletionId.value, _destroy: true }] },
      },
    });

    if (ok) {
      issue.issue_images = sortedImages.value.filter((img) => img.id !== deletionId.value);
      imageValid.value = true;

      issueStore.deleteImage(issue.id, deletionId.value);
    }
  }
  updateCarousel();
  dismissDialogAction();
}

async function loadRegions() {
  const isHme = issue?.location_type === 'Plants::HeavyMobileEquipmentLocation';
  const url = isHme ? operationData.value.hmeRegionsUrl : operationData.value.regionsUrl;
  let { ok, data } = await ajax(url);

  if (ok) {
    regions.value = data.regions;
  }
}

function setAssessment(image) {
  /**
   * If the AI icon on the thumbnail image is clicked
   * it preselects AIDA tab when damage calculator opens
   */
  router.replace({ query: { tab: 'ai_info' } });
  viewingImage.value = image;
}

function openDamageCalculation(image_id) {
  router.replace({ query: { tab: 'damage_calculator' } });
  viewingImage.value = issue.issue_images.find((image) => image.id === image_id);
}

async function sendToSap() {
  sendToSapLoading.value = true;
  let { ok, data } = await ajax(`${operationData.value.issueUrl}/send_to_sap`, {
    method: 'POST',
  });

  if (ok) {
    errors.value = null;
    await loadInitialIssue();
  } else {
    errors.value = data;
  }
  sendToSapLoading.value = false;
}

function cleanSapData(data, fieldList, removeNullField = true) {
  let filteredData = {};
  fieldList && fieldList.forEach(field => {
    /**
     * If the field is of type status and it's value is Success, it's ignored
     * If removeNullField is set to true then it will return all provided in the `fieldList` with valid values (0 included),
     * else it returns all fields provided in the `fieldList` regardless of the validity of their values
     */
    const condition = removeNullField ? data[field]?.toString() && data[field] !== 'Success' : data[field] !== 'Success';
    condition && (filteredData = { ...filteredData, [field]: data[field]?.toString() });
  });
  /**
   * If filteredData is empty we return the original data
   */
  const newData = Object.keys(filteredData).length !== 0 ? filteredData : data;
  return Object.entries(newData);
}

/**
 * Check if the back route is of inspections,
 * if it is we want to use the route instead of
 * the router.back() method to avoid a loop in
 * navigation between the location page and the issue form
*/
function useRouterPath(back) {
  if (!back) return true;
  return back.includes('/inspections/');
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';

.error {
  color: map-get($error-color, default);
}

.disabled {
  cursor: not-allowed;
}
</style>
